/* apps/web/src/branding/logoColors.ts
 *
 * The two strongest colours in a logo.
 *
 * The canvas's Branding tab offers "From the logo — use these" beside the
 * colour fields, and this is the routine behind it, transcribed from the
 * canvas: draw the image into a 64×64 canvas, bin the pixels by their top four
 * bits per channel, weight each bin by saturation so a small saturated mark
 * beats a large grey field, and return the strongest bin plus the strongest
 * one far enough away from it to read as a second colour.
 *
 * Near-white and near-black pixels are dropped: they are the paper and the
 * ink, not the brand.
 */

/** Why the colours could not be read, in words a person can act on. */
export type LogoColorsFailure =
  | { reason: "empty"; message: string }
  | { reason: "tainted"; message: string }
  | { reason: "load"; message: string };

export type LogoColorsResult =
  | { ok: true; colors: string[] }
  | ({ ok: false } & LogoColorsFailure);

interface Bin {
  weight: number;
  r: number;
  g: number;
  b: number;
  count: number;
}

function hex(rgb: [number, number, number]): string {
  return `#${rgb.map((value) => value.toString(16).padStart(2, "0")).join("")}`;
}

function manhattan(a: [number, number, number], b: [number, number, number]): number {
  return Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) + Math.abs(a[2] - b[2]);
}

function extract(image: HTMLImageElement): LogoColorsResult {
  const surface = document.createElement("canvas");
  surface.width = 64;
  surface.height = 64;
  const context = surface.getContext("2d");
  if (!context) {
    return { ok: false, reason: "empty", message: "This browser could not read the logo." };
  }
  context.drawImage(image, 0, 0, 64, 64);

  let data: Uint8ClampedArray;
  try {
    data = context.getImageData(0, 0, 64, 64).data;
  } catch {
    // A cross-origin image without CORS headers taints the canvas. Nothing can
    // be read from it, and the fix is the user's: pick the colours by hand.
    return {
      ok: false,
      reason: "tainted",
      message:
        "This logo is hosted without CORS access, so its colours can't be read. " +
        "Pick the colours above by hand.",
    };
  }

  const bins = new Map<string, Bin>();
  for (let i = 0; i < data.length; i += 4) {
    if ((data[i + 3] ?? 0) < 128) continue;
    const r = data[i] ?? 0;
    const g = data[i + 1] ?? 0;
    const b = data[i + 2] ?? 0;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    // Paper and ink.
    if (max > 242 && min > 232) continue;
    if (max < 24) continue;
    const saturation = max === 0 ? 0 : (max - min) / max;
    const key = `${r >> 4},${g >> 4},${b >> 4}`;
    const bin = bins.get(key) ?? { weight: 0, r: 0, g: 0, b: 0, count: 0 };
    bin.weight += 1 + saturation * 2.5;
    bin.r += r;
    bin.g += g;
    bin.b += b;
    bin.count += 1;
    bins.set(key, bin);
  }

  const ranked = [...bins.values()]
    .map((bin) => {
      const rgb: [number, number, number] = [
        Math.round(bin.r / bin.count),
        Math.round(bin.g / bin.count),
        Math.round(bin.b / bin.count),
      ];
      return { hex: hex(rgb), rgb, weight: bin.weight };
    })
    .sort((a, b) => b.weight - a.weight);

  const first = ranked[0];
  if (!first) {
    return { ok: false, reason: "empty", message: "No usable colours in this logo." };
  }
  const second =
    ranked.slice(1).find((candidate) => manhattan(candidate.rgb, first.rgb) > 90) ?? ranked[1];

  return { ok: true, colors: second ? [first.hex, second.hex] : [first.hex] };
}

/** Reads `src` — a URL, a path or a data URI — and resolves its brand colours. */
export function readLogoColors(src: string): Promise<LogoColorsResult> {
  return new Promise((resolve) => {
    if (!src) {
      resolve({ ok: false, reason: "empty", message: "No logo to read." });
      return;
    }
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(extract(image));
    image.onerror = () =>
      resolve({ ok: false, reason: "load", message: "That logo couldn't be loaded." });
    image.src = src;
  });
}
